import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, unwrap } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { businessDate, money } from '../lib/format';
import { useShop } from '../shop/ShopProvider';
import type { LinkedGroup } from './MoreMenu';

/** A row of any document list, as far as the ⋯ menu needs it (returns and receipts carry only cost, no total). */
interface Doc {
  id: string;
  number: string;
  docDate: string;
  status: string;
  total?: string;
}

type Filter = Partial<
  Record<
    | 'contactId'
    | 'salesOrderId'
    | 'salesInvoiceId'
    | 'purchaseOrderId'
    | 'supplierInvoiceId'
    | 'goodsReceiptId'
    | 'deliveryId',
    string
  >
>;

// The menu shows the latest few; the page total gives the count.
const page = { pageSize: 20 };

/** Every kind of document a record can link to: how to list it, its screen, its menu group. */
const KINDS = {
  quotations: {
    title: 'Quotations',
    path: '/quotations',
    permission: 'quotations.view',
    icon: <RequestQuoteRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/quotations', { params: { query: { ...f, ...page } } })),
  },
  orders: {
    title: 'Sales orders',
    path: '/sales/orders',
    permission: 'sales.view',
    icon: <ShoppingBagRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/sales-orders', { params: { query: { ...f, ...page } } })),
  },
  deliveries: {
    title: 'Deliveries',
    path: '/sales/deliveries',
    permission: 'sales.view',
    icon: <LocalShippingRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/deliveries', { params: { query: { ...f, ...page } } })),
  },
  invoices: {
    title: 'Invoices',
    path: '/sales/invoices',
    permission: 'sales.view',
    icon: <ReceiptLongRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/sales-invoices', { params: { query: { ...f, ...page } } })),
  },
  creditNotes: {
    title: 'Credit notes',
    path: '/returns/credit-notes',
    permission: 'sales.view',
    icon: <UndoRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/credit-notes', { params: { query: { ...f, ...page } } })),
  },
  salesReturns: {
    title: 'Returns',
    path: '/returns/sales',
    permission: 'sales.view',
    icon: <AssignmentReturnRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/sales-returns', { params: { query: { ...f, ...page } } })),
  },
  purchaseOrders: {
    title: 'Purchase orders',
    path: '/purchases/orders',
    permission: 'purchases.view',
    icon: <ShoppingBagRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/purchase-orders', { params: { query: { ...f, ...page } } })),
  },
  receipts: {
    title: 'Goods receipts',
    path: '/purchases/receipts',
    permission: 'purchases.view',
    icon: <Inventory2Rounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/goods-receipts', { params: { query: { ...f, ...page } } })),
  },
  bills: {
    title: 'Bills',
    path: '/purchases/bills',
    permission: 'purchases.view',
    icon: <ReceiptLongRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/supplier-invoices', { params: { query: { ...f, ...page } } })),
  },
  debitNotes: {
    title: 'Debit notes',
    path: '/returns/debit-notes',
    permission: 'purchases.view',
    icon: <UndoRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/debit-notes', { params: { query: { ...f, ...page } } })),
  },
  purchaseReturns: {
    title: 'Returns',
    path: '/returns/purchase',
    permission: 'purchases.view',
    icon: <AssignmentReturnRounded fontSize="small" />,
    list: async (f: Filter) =>
      await unwrap(api.GET('/purchase-returns', { params: { query: { ...f, ...page } } })),
  },
} satisfies Record<
  string,
  {
    title: string;
    /** Its detail screen, followed by /:id. */
    path: string;
    permission: string;
    icon: ReactNode;
    list: (f: Filter) => Promise<{ data: Doc[]; page: { total: number } }>;
  }
>;

export type LinkedKind = keyof typeof KINDS;

/** "12 Oct 2026 · Rs 1,200 · cancelled": date, total when it has one, and a void status. */
function hint(d: Doc, format: Parameters<typeof money>[1]) {
  return [
    businessDate(d.docDate, format),
    d.total !== undefined ? money(d.total, format) : null,
    ['CANCELLED', 'REVERSED', 'VOIDED'].includes(d.status) ? d.status.toLowerCase() : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The documents of one kind linked to a record (e.g. a sales order's deliveries), as a ⋯ menu
 * group. Not fetched without the permission to see them, or while `filter` is null.
 */
export function useLinked(kind: LinkedKind, filter: Filter | null): LinkedGroup {
  const { can } = useAuth();
  const { format } = useShop();
  const k = KINDS[kind];
  const list: (f: Filter) => Promise<{ data: Doc[]; page: { total: number } }> = k.list;
  const q = useQuery({
    queryKey: ['linked', kind, filter],
    queryFn: () => list(filter ?? {}),
    enabled: filter !== null && can(k.permission),
  });
  return {
    key: kind,
    title: k.title,
    icon: k.icon,
    total: q.data?.page.total,
    docs: (q.data?.data ?? []).flatMap((d) => {
      return [{ id: d.id, label: d.number, hint: hint(d, format), to: `${k.path}/${d.id}` }];
    }),
  };
}

const ONE = {
  order: {
    title: 'Sales order',
    kind: 'orders',
    get: (id: string) => unwrap(api.GET('/sales-orders/{id}', { params: { path: { id } } })),
  },
  delivery: {
    title: 'Delivery',
    kind: 'deliveries',
    get: (id: string) => unwrap(api.GET('/deliveries/{id}', { params: { path: { id } } })),
  },
  invoice: {
    title: 'Invoice',
    kind: 'invoices',
    get: (id: string) => unwrap(api.GET('/sales-invoices/{id}', { params: { path: { id } } })),
  },
  quotation: {
    title: 'Quotation',
    kind: 'quotations',
    get: (id: string) => unwrap(api.GET('/quotations/{id}', { params: { path: { id } } })),
  },
  purchaseOrder: {
    title: 'Purchase order',
    kind: 'purchaseOrders',
    get: (id: string) => unwrap(api.GET('/purchase-orders/{id}', { params: { path: { id } } })),
  },
  receipt: {
    title: 'Goods receipt',
    kind: 'receipts',
    get: (id: string) => unwrap(api.GET('/goods-receipts/{id}', { params: { path: { id } } })),
  },
  bill: {
    title: 'Bill',
    kind: 'bills',
    get: (id: string) => unwrap(api.GET('/supplier-invoices/{id}', { params: { path: { id } } })),
  },
  salesReturn: {
    title: 'Return',
    kind: 'salesReturns',
    get: (id: string) => unwrap(api.GET('/sales-returns/{id}', { params: { path: { id } } })),
  },
  purchaseReturn: {
    title: 'Return',
    kind: 'purchaseReturns',
    get: (id: string) => unwrap(api.GET('/purchase-returns/{id}', { params: { path: { id } } })),
  },
} satisfies Record<string, { title: string; kind: LinkedKind; get: (id: string) => Promise<Doc> }>;

/** The one document a record comes from (an invoice's order, a receipt's PO), as a ⋯ menu group. */
export function useLinkedOne(kind: keyof typeof ONE, id: string | null | undefined): LinkedGroup {
  const { can } = useAuth();
  const { format } = useShop();
  const one = ONE[kind];
  const k = KINDS[one.kind];
  const get: (id: string) => Promise<Doc> = one.get;
  const q = useQuery({
    queryKey: ['linked-one', kind, id],
    queryFn: () => get(id ?? ''),
    enabled: !!id && can(k.permission),
  });
  return {
    key: kind,
    title: one.title,
    icon: k.icon,
    docs: q.data
      ? [
          {
            id: q.data.id,
            label: q.data.number,
            hint: hint(q.data, format),
            to: `${k.path}/${q.data.id}`,
          },
        ]
      : [],
  };
}

/** Linked documents the record already carries (a return's invoice), as a ⋯ menu group. */
export function linkedKnown(
  kind: LinkedKind,
  docs: ({ id: string; number: string | null } | null | undefined)[],
  title: string = KINDS[kind].title,
): LinkedGroup {
  const k = KINDS[kind];
  const seen = new Set<string>();
  return {
    key: kind,
    title,
    icon: k.icon,
    docs: docs.flatMap((d) => {
      if (!d || seen.has(d.id)) return [];
      seen.add(d.id);
      return [{ id: d.id, label: d.number ?? title, to: `${k.path}/${d.id}` }];
    }),
  };
}

/** The invoices and bills a payment or note settles (its active allocations), as ⋯ menu groups. */
export function linkedAllocations(
  allocations: { target: { type: string; id: string; number: string | null }; reversed: boolean }[],
): LinkedGroup[] {
  const active = allocations.filter((a) => !a.reversed).map((a) => a.target);
  return [
    linkedKnown(
      'invoices',
      active.filter((t) => t.type === 'SALES_INVOICE'),
      'Settles invoices',
    ),
    linkedKnown(
      'bills',
      active.filter((t) => t.type === 'SUPPLIER_INVOICE'),
      'Settles bills',
    ),
  ];
}
